import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Copy, Check, ExternalLink, Package, Pencil, Trash2, X, Info } from "lucide-react";
import {
  useGetSkills,
  useGetSkillMd,
  useInstallSkill,
  useUpdateSkill,
  useRemoveSkill,
  usePreviewSkill,
  useGetUserDetails,
  useSaveSkillCredential,
} from "../../../api/queryHooks";
import { getSkillCredential } from "../../../api/providers";
import UserAuthStore from "../../../store/userStore";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";
import AlertBanner from "../../shared/AlertBanner/AlertBanner";
import InlineSpinner from "../../shared/Spinner/InlineSpinner";
import CredentialField from "../../Settings/CredentialField";
import logger from "../../../services/Logger";

const SCOPE_LABELS = { agent_only: "Agent only", all: "Org-wide" };

// Shared collapsible package group container — used by both preview and installed sections
function SkillPackageGroup({ name, version, skillCount, homepage, isCollapsed, onToggle, headerActions, bodyClassName, className, children }) {
  return (
    <div className={className ?? "bg-fill-white dark:bg-fill-hex-232326 rounded-2xl border border-edge-gray-200 dark:border-edge-panel-border overflow-hidden"}>
      <div
        className="flex items-center justify-between px-5 py-3 bg-fill-gray-50 dark:bg-fill-deep-surface cursor-pointer select-none"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Package className="w-4 h-4 text-ink-primary" />
          <span className="text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">{name}</span>
          {version && <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500">v{version}</span>}
          <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500">
            {skillCount} skill{skillCount !== 1 ? "s" : ""}
          </span>
          {homepage && (
            <a
              href={homepage}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 text-xs text-ink-primary hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {headerActions}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="p-1 rounded hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 transition-colors"
          >
            {isCollapsed
              ? <ChevronDown className="w-3.5 h-3.5 text-ink-gray-400" />
              : <ChevronUp className="w-3.5 h-3.5 text-ink-gray-400" />}
          </button>
        </div>
      </div>
      {!isCollapsed && (
        <div className={bodyClassName}>
          {children}
        </div>
      )}
    </div>
  );
}

// Renders SKILL.md content for an installed skill with copy button
function InstalledSkillMd({ orgId, skill, copiedSkill, setCopiedSkill }) {
  const { data, isLoading } = useGetSkillMd(orgId, skill.skill_id);
  const skillMd = data?.skill_md || "";

  if (isLoading) {
    return <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 italic">Loading SKILL.md…</p>;
  }
  if (!skillMd) {
    return (
      <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 italic">No SKILL.md available.</p>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(skillMd);
          setCopiedSkill(skill.skill_id);
          setTimeout(() => setCopiedSkill(null), 1500);
        }}
        className="absolute top-1 right-1 p-1.5 rounded-md bg-fill-white/80 dark:bg-fill-gray-800/80 hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 text-ink-gray-500 hover:text-ink-gray-700 dark:hover:text-ink-gray-300 transition-colors z-10"
        title="Copy SKILL.md"
      >
        {copiedSkill === skill.skill_id ? (
          <Check className="w-3.5 h-3.5 text-ink-green-500" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
      <pre className="text-[11px] text-ink-gray-700 dark:text-ink-gray-300 bg-fill-gray-50 dark:bg-fill-hex-1a1a1c rounded-lg p-3 overflow-auto min-h-[120px] max-h-[80vh] whitespace-pre-wrap font-mono leading-relaxed resize-y pr-8">
        {skillMd}
      </pre>
    </div>
  );
}

const ForgeSkillsSection = ({
  showHeader = true,
  installPanelOpen = true,
  onCloseInstallPanel,
}) => {
  const { orgId, user } = UserAuthStore.getState();
  const { data: userDetails } = useGetUserDetails(orgId, user?.uid);
  const { data: skillsData, isLoading, refetch } = useGetSkills(orgId);
  const skills = useMemo(() => skillsData?.skills || [], [skillsData]);
  const installedByName = Object.fromEntries(skills.map((s) => [s.skill_name, s]));
  const isAdmin = userDetails?.org_mode === true;

  // Install panel
  const [command, setCommand] = useState("");
  const [preview, setPreview] = useState(null);
  const [expandedSkillName, setExpandedSkillName] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState(new Set());
  const [skillScopes, setSkillScopes] = useState({});
  const [skillSearch, setSkillSearch] = useState("");
  const [copiedSkill, setCopiedSkill] = useState(null);
  const [installError, setInstallError] = useState(null);
  const [installSummary, setInstallSummary] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Edit modal
  const [editingSkill, setEditingSkill] = useState(null);
  const [editError, setEditError] = useState(null);

  // Remove confirmation
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [removeBlockedAgents, setRemoveBlockedAgents] = useState(null);
  const [collapsedPackages, setCollapsedPackages] = useState(new Set());
  const [expandedInstalledSkill, setExpandedInstalledSkill] = useState(null);
  const [installedSearch, setInstalledSearch] = useState("");
  const [selectedInstalledSkillIds, setSelectedInstalledSkillIds] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateError, setBulkUpdateError] = useState(null);
  const [bulkUpdateSummary, setBulkUpdateSummary] = useState(null);

  const togglePackage = (key) => setCollapsedPackages((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const closeInstallPanel = () => {
    setCommand("");
    setPreview(null);
    setSelectedSkills(new Set());
    setSkillScopes({});
    setSkillSearch("");
    setExpandedSkillName(null);
    setInstallError(null);
    setInstallSummary(null);
    setPreviewError(null);
    onCloseInstallPanel?.();
  };

  const installMutation = useInstallSkill({
    onSuccess: (data) => {
      refetch();
      setInstallError(null);
      setInstallSummary(data);
      if (!data?.conflicts?.length) closeInstallPanel();
    },
    onError: (err) => {
      setInstallError(err.response?.data?.detail || err.message || "Failed to install skill");
      logger.error("ForgeSkillsSection", "Install failed", { error: err.message });
    },
  });

  const previewMutation = usePreviewSkill({
    onSuccess: (data) => {
      const previewData = data?.preview || {};
      setPreview(previewData);
      setExpandedSkillName(null);
      setSelectedSkills(new Set());
      setSkillSearch("");
      setInstallSummary(null);
      setPreviewError(null);
    },
    onError: (err) => {
      setPreviewError(err.response?.data?.detail || err.message || "Failed to preview skill");
    },
  });

  const updateMutation = useUpdateSkill({
    onSuccess: () => {
      refetch();
      setEditingSkill(null);
      setEditError(null);
    },
    onError: (err) => {
      setEditError(err.response?.data?.detail || err.message || "Failed to update skill");
    },
  });
  const bulkUpdateMutation = useUpdateSkill();

  const saveCredentialMutation = useSaveSkillCredential();

  const removeMutation = useRemoveSkill({
    onSuccess: () => {
      refetch();
      setRemoveConfirm(null);
      setRemoveError(null);
      setRemoveBlockedAgents(null);
      setInstallSummary(null);
    },
    onError: (err) => {
      if (err.response?.status === 409 && err.response?.data?.agents) {
        setRemoveBlockedAgents(err.response.data.agents);
      } else {
        setRemoveError(err.response?.data?.detail || err.message || "Failed to remove skill");
      }
    },
  });

  const handlePreview = () => {
    if (!command.trim()) return;
    setPreviewError(null);
    previewMutation.mutate({ command: command.trim() });
  };

  const handleInstall = () => {
    if (!command.trim() || selectedSkills.size === 0) return;
    setInstallError(null);
    const selectedScopeMap = Object.fromEntries(
      [...selectedSkills].map((name) => [name, skillScopes[name] || "all"])
    );
    installMutation.mutate({ orgId, uid: user.uid, command: command.trim(), selectedSkills: [...selectedSkills], skillScopes: selectedScopeMap });
  };

  const toggleSkillSelection = (name) => {
    const inst = installedByName[name];
    const isConflict = inst && inst.source_name !== (preview?.source?.name || "");
    if (isConflict) return;
    setSelectedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const filteredSkills = preview?.skills
    ? Object.entries(preview.skills).filter(([name]) =>
        name.toLowerCase().includes(skillSearch.toLowerCase())
      )
    : [];
  const filteredSkillNames = filteredSkills.map(([name]) => name);
  const previewSourceName = preview?.source?.name || "";
  const previewSourceVersion = preview?.source?.version || "";
  const getPreviewSkillState = (name) => {
    const inst = installedByName[name];
    if (!inst) return "new";
    if (inst.source_name !== previewSourceName) return "conflict";
    if (inst.source_version === previewSourceVersion) return "already_installed";
    return "update_available";
  };
  const selectableSkillNames = filteredSkillNames.filter((n) => getPreviewSkillState(n) !== "conflict");
  const allSelected = selectableSkillNames.length > 0 && selectableSkillNames.every((n) => selectedSkills.has(n));
  const toggleSelectAll = () => {
    setSelectedSkills((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableSkillNames.forEach((n) => next.delete(n));
      } else {
        selectableSkillNames.forEach((n) => next.add(n));
      }
      return next;
    });
  };
  const selectedStateCounts = [...selectedSkills].reduce((acc, name) => {
    const state = getPreviewSkillState(name);
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});
  const installBtnLabel = (() => {
    if (selectedSkills.size === 0) return "Install";
    const newCount = selectedStateCounts.new || 0;
    const updateCount = selectedStateCounts.update_available || 0;
    if (newCount > 0 && updateCount > 0)
      return `Apply (${newCount} new, ${updateCount} update${updateCount !== 1 ? "s" : ""})`;
    if (updateCount > 0)
      return `Update ${updateCount} skill${updateCount !== 1 ? "s" : ""}`;
    return `Install ${selectedSkills.size} skill${selectedSkills.size !== 1 ? "s" : ""}`;
  })();

  const handleSaveEdit = () => {
    if (!editingSkill) return;
    const { skill_id, ...updates } = editingSkill;
    updateMutation.mutate({ orgId, uid: user.uid, skillId: skill_id, updates });
  };

  const handleRemove = (force = false) => {
    if (!removeConfirm) return;
    setRemoveError(null);
    removeMutation.mutate({ orgId, uid: user.uid, skillId: removeConfirm.skill_id, force });
  };

  const filteredInstalledSkills = useMemo(() => {
    const query = installedSearch.trim().toLowerCase();
    if (!query) return skills;
    return skills.filter((skill) => [
      skill.display_name,
      skill.skill_name,
      skill.description,
      skill.source_name,
    ].filter(Boolean).some((value) => value.toLowerCase().includes(query)));
  }, [installedSearch, skills]);

  const visibleInstalledSkillIds = filteredInstalledSkills.map((skill) => skill.skill_id);
  const allVisibleInstalledSelected = visibleInstalledSkillIds.length > 0
    && visibleInstalledSkillIds.every((skillId) => selectedInstalledSkillIds.has(skillId));

  const toggleInstalledSkill = (skillId) => {
    setSelectedInstalledSkillIds((previous) => {
      const next = new Set(previous);
      if (next.has(skillId)) next.delete(skillId); else next.add(skillId);
      return next;
    });
    setBulkUpdateError(null);
    setBulkUpdateSummary(null);
  };

  const toggleAllVisibleInstalled = () => {
    setSelectedInstalledSkillIds((previous) => {
      const next = new Set(previous);
      visibleInstalledSkillIds.forEach((skillId) => {
        if (allVisibleInstalledSelected) next.delete(skillId); else next.add(skillId);
      });
      return next;
    });
    setBulkUpdateError(null);
    setBulkUpdateSummary(null);
  };

  const handleBulkUpdate = async (updates) => {
    const selected = skills.filter((skill) => selectedInstalledSkillIds.has(skill.skill_id));
    if (selected.length === 0) return;

    setIsBulkUpdating(true);
    setBulkUpdateError(null);
    setBulkUpdateSummary(null);
    try {
      const results = await Promise.allSettled(selected.map((skill) =>
        bulkUpdateMutation.mutateAsync({
          orgId,
          uid: user.uid,
          skillId: skill.skill_id,
          updates,
        })
      ));
      const failed = results
        .map((result, index) => result.status === "rejected" ? selected[index] : null)
        .filter(Boolean);
      const succeededCount = selected.length - failed.length;

      if (succeededCount > 0) {
        await refetch();
        setBulkUpdateSummary(`${succeededCount} skill${succeededCount === 1 ? "" : "s"} updated.`);
      }

      setSelectedInstalledSkillIds(new Set(failed.map((skill) => skill.skill_id)));
      if (failed.length > 0) {
        setBulkUpdateError(
          `Failed to update ${failed.length} skill${failed.length === 1 ? "" : "s"}: ${failed.map((skill) => skill.display_name).join(", ")}.`
        );
      }
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return (
    <div className={showHeader ? "mt-12 pb-16" : "pb-16"}>
      {showHeader && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <h3 className="font-semibold text-2xl text-ink-ink dark:text-ink-white">Skills</h3>
          </div>
          <p className="text-ink-gray-500 dark:text-ink-gray-400 text-sm mt-2 mb-6">
            Reusable instruction sets that extend agent capabilities. Installed from GitHub via{" "}
            <code className="bg-fill-gray-100 dark:bg-fill-gray-700 px-1 py-0.5 rounded text-xs font-mono">npx skills</code>.
          </p>
        </>
      )}

      {/* Admin install panel */}
      {isAdmin && installPanelOpen && (
        <div className="bg-fill-white dark:bg-fill-hex-232326 rounded-2xl p-5 border border-edge-gray-200 dark:border-edge-panel-border mb-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              <h4 className="text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">Install Skill</h4>
              <div className="relative group">
                <Info className="w-3.5 h-3.5 text-ink-gray-400 dark:text-ink-gray-500 cursor-default" />
                <div className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 w-72 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <div className="bg-fill-gray-900 dark:bg-fill-gray-700 text-ink-gray-100 text-[11px] rounded-lg px-3 py-2.5 shadow-lg leading-relaxed">
                    Supports npm packages{" "}
                    <code className="bg-fill-gray-700 dark:bg-fill-gray-600 px-1 py-0.5 rounded font-mono text-[10px]">npx @scope/package</code>
                    {" "}or GitHub repos{" "}
                    <code className="bg-fill-gray-700 dark:bg-fill-gray-600 px-1 py-0.5 rounded font-mono text-[10px]">npx skills add org/repo</code>.
                    {" "}Hit <kbd className="bg-fill-gray-700 dark:bg-fill-gray-600 px-1 py-0.5 rounded font-mono text-[10px]">Enter</kbd> or click Preview to inspect before installing.
                  </div>
                </div>
              </div>
            </div>
            {onCloseInstallPanel && (
              <button
                type="button"
                onClick={closeInstallPanel}
                aria-label="Close install skills"
                className="rounded-md p-1.5 text-ink-gray-400 transition-colors hover:bg-fill-gray-100 hover:text-ink-gray-600 dark:hover:bg-fill-gray-700 dark:hover:text-ink-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={command}
                onChange={(e) => { setCommand(e.target.value); setPreview(null); setSelectedSkills(new Set()); setSkillSearch(""); setExpandedSkillName(null); setInstallSummary(null); }}
                onKeyDown={(e) => e.key === "Enter" && handlePreview()}
                placeholder="npx <skill-package>"
                className="photon-field w-full px-4 py-2.5 pr-8 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-white text-sm focus:ring-2 focus:ring-focus-primary focus:border-transparent"
              />
              {command && (
                <button
                  type="button"
                  onClick={() => { setCommand(""); setPreview(null); setSelectedSkills(new Set()); setSkillSearch(""); setExpandedSkillName(null); setInstallSummary(null); setInstallError(null); setPreviewError(null); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-gray-300 transition-colors"
                  aria-label="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handlePreview}
              disabled={!command.trim() || previewMutation.isPending}
              className="px-4 py-2.5 text-sm font-medium text-ink-primary border border-edge-primary rounded-lg hover:bg-fill-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 whitespace-nowrap"
            >
              {previewMutation.isPending && <InlineSpinner />}
              Preview
            </button>
          </div>

          {previewError && <AlertBanner className="mt-3">{previewError}</AlertBanner>}
          {installError && <AlertBanner className="mt-3">{installError}</AlertBanner>}
          {installSummary && (
            <div className="mt-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-gray-50 dark:bg-fill-deep-surface px-4 py-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {installSummary.created?.length > 0 && (
                  <span className="text-ink-green-600 dark:text-ink-green-400 font-medium">
                    {installSummary.created.length} installed
                  </span>
                )}
                {installSummary.updated?.length > 0 && (
                  <span className="text-ink-primary dark:text-ink-blue-300 font-medium">
                    {installSummary.updated.length} updated
                  </span>
                )}
                {installSummary.already_installed?.length > 0 && (
                  <span className="text-ink-gray-500 dark:text-ink-gray-400">
                    {installSummary.already_installed.length} already up to date
                  </span>
                )}
                {installSummary.conflicts?.length > 0 && (
                  <span className="text-ink-hex-d97706 dark:text-ink-amber-400 font-medium">
                    {installSummary.conflicts.length} conflict{installSummary.conflicts.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {installSummary.conflicts?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {installSummary.conflicts.map((c) => (
                    <p key={c.skill_id || c.skill_name} className="text-[10px] text-ink-hex-d97706 dark:text-ink-amber-400">
                      <span className="font-semibold">{c.skill_name}</span> — already installed from a different source. Remove it first to switch.
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {previewMutation.isPending && (
            <div className="mt-4 flex items-center gap-2 text-sm text-ink-gray-500 dark:text-ink-gray-400">
              <InlineSpinner size="sm" className="text-ink-primary" />
              <span>Fetching skills…</span>
            </div>
          )}

          {!previewMutation.isPending && preview !== null && preview.skills && (
            <SkillPackageGroup
              name={preview.source?.name || command}
              version={preview.source?.version}
              skillCount={preview.skill_count || Object.keys(preview.skills).length}
              homepage={preview.source?.homepage}
              className="mt-4 overflow-hidden border border-edge-gray-200 dark:border-edge-panel-border rounded-xl"
              isCollapsed={collapsedPackages.has(preview.source?.name || command)}
              onToggle={() => togglePackage(preview.source?.name || command)}
              bodyClassName="px-5 py-4 space-y-3"
              headerActions={
                <div className="flex items-center gap-3">
                  <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500">
                    {selectedSkills.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className="text-xs text-ink-primary hover:underline"
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                  <button
                    type="button"
                    onClick={handleInstall}
                    disabled={selectedSkills.size === 0 || installMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium text-ink-on-primary bg-fill-primary hover:bg-fill-primary-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                  >
                    {installMutation.isPending && <InlineSpinner size="xs" />}
                    {installBtnLabel}
                  </button>
                </div>
              }
            >
              {/* Selected chips */}
              {selectedSkills.size > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {[...selectedSkills].map((name) => (
                    <span
                      key={name}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-fill-primary/10 text-ink-primary dark:bg-fill-primary/20 dark:text-ink-blue-300"
                    >
                      {name}
                      <button
                        type="button"
                        onClick={() => toggleSkillSelection(name)}
                        className="ml-0.5 rounded-full hover:bg-fill-primary/20 transition-colors leading-none"
                        aria-label={`Deselect ${name}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {/* Search */}
              <input
                type="text"
                value={skillSearch}
                onChange={(e) => setSkillSearch(e.target.value)}
                placeholder="Search skills…"
                className="photon-field w-full mb-2 px-3 py-2 text-sm border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent"
              />
              {filteredSkills.length === 0 && skillSearch && (
                <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 text-center py-4">No skills match "{skillSearch}"</p>
              )}
              {/* Skill list — plain stack so each row can own its inline SKILL.md expansion */}
              <div className="space-y-1 max-h-[440px] overflow-auto rounded-lg">
                {filteredSkills.map(([name, skill]) => (
                  <div
                    key={name}
                    className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg overflow-hidden"
                  >
                    {/* Row header */}
                    <div className="w-full flex items-center justify-between px-4 py-2.5 bg-fill-gray-50 dark:bg-fill-deep-surface text-left">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedSkills.has(name)}
                          onChange={() => toggleSkillSelection(name)}
                          disabled={getPreviewSkillState(name) === "conflict"}
                          className="w-3.5 h-3.5 accent-fill-primary cursor-pointer flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm font-medium text-ink-ink dark:text-ink-white">{name}</span>
                        {getPreviewSkillState(name) === "already_installed" && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-800 dark:text-ink-gray-400">
                            ✓ Installed
                          </span>
                        )}
                        {getPreviewSkillState(name) === "update_available" && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium bg-fill-hex-fef3c7 text-ink-amber-700 dark:bg-fill-hex-78350f/30 dark:text-ink-amber-400">
                            ↑ Update
                          </span>
                        )}
                        {getPreviewSkillState(name) === "conflict" && (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium bg-fill-red-100 text-ink-red-800 dark:bg-fill-red-900/30 dark:text-ink-red-400">
                            ⚠ Conflict
                          </span>
                        )}
                        {skill.skill_md ? (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-900/30 dark:text-ink-green-400">
                            SKILL.md
                          </span>
                        ) : (
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium bg-fill-gray-100 text-ink-gray-500 dark:bg-fill-gray-800 dark:text-ink-gray-500">
                            no SKILL.md
                          </span>
                        )}
                        <span className="text-[10px] text-ink-gray-400">
                          {skill.files?.length || 0} file{(skill.files?.length || 0) !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {selectedSkills.has(name) && getPreviewSkillState(name) !== "conflict" && (
                          <div className="flex items-center rounded overflow-hidden border border-edge-gray-200 dark:border-edge-gray-700 text-[10px] font-medium">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSkillScopes((prev) => ({ ...prev, [name]: "all" })); }}
                              className={`px-2 py-1 transition-colors ${
                                (skillScopes[name] || "all") === "all"
                                  ? "bg-fill-primary text-ink-on-primary"
                                  : "bg-fill-white dark:bg-fill-deep-surface text-ink-gray-500 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
                              }`}
                            >
                              Org-wide
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSkillScopes((prev) => ({ ...prev, [name]: "agent_only" })); }}
                              className={`px-2 py-1 transition-colors border-l border-edge-gray-200 dark:border-edge-gray-700 ${
                                (skillScopes[name] || "all") === "agent_only"
                                  ? "bg-fill-purple-600 text-ink-on-color"
                                  : "bg-fill-white dark:bg-fill-deep-surface text-ink-gray-500 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
                              }`}
                            >
                              Agent only
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedSkillName((prev) => (prev === name ? null : name))}
                          className="p-1 rounded hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 transition-colors"
                        >
                          {expandedSkillName === name
                            ? <ChevronUp className="w-3.5 h-3.5 text-ink-gray-400" />
                            : <ChevronDown className="w-3.5 h-3.5 text-ink-gray-400" />}
                        </button>
                      </div>
                    </div>
                    {/* Inline SKILL.md expansion — directly below this row */}
                    {expandedSkillName === name && (
                      <div className="px-4 py-3 border-t border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-hex-1a1a1c">
                        {skill.skill_md ? (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(skill.skill_md);
                                setCopiedSkill(name);
                                setTimeout(() => setCopiedSkill(null), 2000);
                              }}
                              className="absolute top-1 right-1 p-1.5 rounded-md bg-fill-white/80 dark:bg-fill-gray-800/80 hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 text-ink-gray-500 hover:text-ink-gray-700 dark:hover:text-ink-gray-300 transition-colors z-10"
                              title="Copy SKILL.md"
                            >
                              {copiedSkill === name
                                ? <Check className="w-3.5 h-3.5 text-ink-green-500" />
                                : <Copy className="w-3.5 h-3.5" />}
                            </button>
                            <pre className="text-xs text-ink-gray-700 dark:text-ink-gray-300 overflow-auto min-h-[120px] max-h-[60vh] whitespace-pre-wrap leading-relaxed resize-y pr-8">
                              {skill.skill_md}
                            </pre>
                          </div>
                        ) : (
                          <p className="text-xs text-ink-gray-400 italic">No SKILL.md content available</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SkillPackageGroup>
          )}
        </div>
      )}

      {/* Installed skills — grouped by package, same layout as preview */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-ink-gray-400 text-sm gap-2">
          <InlineSpinner />
          Loading skills...
        </div>
      ) : skills.length === 0 ? (
        <div className="bg-fill-gray-50 dark:bg-fill-hex-232326 rounded-2xl p-10 text-center border-2 border-dashed border-edge-gray-300 dark:border-edge-panel-border">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-fill-gray-200 dark:bg-fill-gray-700 flex items-center justify-center">
              <Package className="w-6 h-6 text-ink-gray-500 dark:text-ink-gray-400" />
            </div>
            <h4 className="font-semibold text-sm text-ink-gray-700 dark:text-ink-gray-200">No skills installed</h4>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 max-w-sm">
              Install skills to extend agent capabilities.{" "}
              {isAdmin
                ? "Use the install panel above to add skills from GitHub."
                : "Ask your org admin to install skills from the Forge page."}
            </p>
          </div>
        </div>
      ) : (() => {
        const selectedCount = selectedInstalledSkillIds.size;
        // Group skills by package (source_name || source_command as fallback key)
        const grouped = filteredInstalledSkills.reduce((acc, skill) => {
          const key = skill.source_name || skill.source_command || "unknown";
          if (!acc[key]) acc[key] = [];
          acc[key].push(skill);
          return acc;
        }, {});

        return (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 bg-fill-white p-3 dark:border-edge-panel-border dark:bg-fill-hex-232326 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative min-w-0 flex-1 sm:max-w-md">
                <input
                  type="search"
                  value={installedSearch}
                  onChange={(event) => setInstalledSearch(event.target.value)}
                  placeholder="Search installed skills"
                  aria-label="Search installed skills"
                  className="photon-field w-full rounded-lg border border-edge-gray-200 bg-fill-white px-3 py-2 text-sm text-ink-ink focus:border-transparent focus:ring-2 focus:ring-focus-primary dark:border-edge-gray-700 dark:bg-fill-deep-surface dark:text-ink-white"
                />
              </div>
              {isAdmin && (
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                    {selectedCount} selected
                  </span>
                  <button
                    type="button"
                    onClick={toggleAllVisibleInstalled}
                    disabled={visibleInstalledSkillIds.length === 0 || isBulkUpdating}
                    className="text-xs font-medium text-ink-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {allVisibleInstalledSelected ? "Deselect visible" : "Select all visible"}
                  </button>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="flex flex-col gap-3 rounded-lg border border-edge-blue-200 bg-fill-blue-50 px-3 py-2.5 dark:border-edge-blue-800 dark:bg-fill-hex-1e3a5f/30 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-ink-blue-700 dark:text-ink-blue-300">
                  {selectedCount > 0
                    ? `Update ${selectedCount} selected skill${selectedCount === 1 ? "" : "s"}`
                    : "Select skills to update"}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => handleBulkUpdate({ enabled: true })} disabled={selectedCount === 0 || isBulkUpdating} className="rounded-md border border-edge-blue-200 bg-fill-white px-2.5 py-1.5 text-xs font-medium text-ink-blue-700 hover:bg-fill-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-edge-blue-800 dark:bg-fill-deep-surface dark:text-ink-blue-300">
                    Enable
                  </button>
                  <button type="button" onClick={() => handleBulkUpdate({ enabled: false })} disabled={selectedCount === 0 || isBulkUpdating} className="rounded-md border border-edge-blue-200 bg-fill-white px-2.5 py-1.5 text-xs font-medium text-ink-blue-700 hover:bg-fill-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-edge-blue-800 dark:bg-fill-deep-surface dark:text-ink-blue-300">
                    Disable
                  </button>
                  <button type="button" onClick={() => handleBulkUpdate({ scope: "all" })} disabled={selectedCount === 0 || isBulkUpdating} className="rounded-md border border-edge-blue-200 bg-fill-white px-2.5 py-1.5 text-xs font-medium text-ink-blue-700 hover:bg-fill-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-edge-blue-800 dark:bg-fill-deep-surface dark:text-ink-blue-300">
                    Org-wide
                  </button>
                  <button type="button" onClick={() => handleBulkUpdate({ scope: "agent_only" })} disabled={selectedCount === 0 || isBulkUpdating} className="rounded-md border border-edge-blue-200 bg-fill-white px-2.5 py-1.5 text-xs font-medium text-ink-blue-700 hover:bg-fill-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-edge-blue-800 dark:bg-fill-deep-surface dark:text-ink-blue-300">
                    Agent only
                  </button>
                  {isBulkUpdating && <InlineSpinner size="xs" />}
                </div>
              </div>
            )}

            {bulkUpdateSummary && (
              <p className="text-xs font-medium text-ink-green-600 dark:text-ink-green-400">{bulkUpdateSummary}</p>
            )}
            {bulkUpdateError && <AlertBanner>{bulkUpdateError}</AlertBanner>}

            {filteredInstalledSkills.length === 0 && (
              <p className="py-8 text-center text-sm text-ink-gray-500 dark:text-ink-gray-400">
                No installed skills match "{installedSearch}".
              </p>
            )}

            {Object.entries(grouped).map(([pkgKey, pkgSkills]) => {
              const repr = pkgSkills[0];
              const pkgLabel = repr.source_name || repr.source_command || "Unknown package";
              const pkgVersion = repr.source_version;
              const pkgHomepage = repr.source_homepage;
              const isCollapsed = installedSearch.trim() ? false : collapsedPackages.has(pkgKey);

              return (
                <SkillPackageGroup
                  key={pkgKey}
                  name={pkgLabel}
                  version={pkgVersion}
                  skillCount={pkgSkills.length}
                  homepage={pkgHomepage}
                  isCollapsed={isCollapsed}
                  onToggle={() => togglePackage(pkgKey)}
                  bodyClassName="divide-y divide-edge-gray-100 dark:divide-edge-gray-800"
                >
                      {pkgSkills.map((skill) => (
                        <div key={skill.skill_id}>
                          {/* Skill row header */}
                          <div className="w-full flex items-center justify-between px-5 py-2.5 bg-fill-white dark:bg-fill-hex-232326 text-left">
                            <div className="flex items-center gap-2 flex-wrap">
                              {isAdmin && (
                                <input
                                  type="checkbox"
                                  checked={selectedInstalledSkillIds.has(skill.skill_id)}
                                  onChange={() => toggleInstalledSkill(skill.skill_id)}
                                  disabled={isBulkUpdating}
                                  aria-label={`Select ${skill.display_name}`}
                                  className="h-3.5 w-3.5 shrink-0 cursor-pointer accent-fill-primary disabled:cursor-not-allowed disabled:opacity-50"
                                />
                              )}
                              <span className="text-sm font-medium text-ink-ink dark:text-ink-white">
                                {skill.display_name}
                              </span>
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                  skill.scope === "agent_only"
                                    ? "bg-fill-hex-f3e8ff text-ink-hex-7e22ce dark:bg-fill-hex-581c87/30 dark:text-ink-hex-c084fc"
                                    : "bg-fill-blue-100 text-ink-primary dark:bg-fill-primary/20 dark:text-ink-blue-300"
                                }`}
                              >
                                {SCOPE_LABELS[skill.scope] || skill.scope}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-[9px] font-medium ${skill.enabled ? "text-ink-green-600 dark:text-ink-green-400" : "text-ink-gray-400 dark:text-ink-gray-500"}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${skill.enabled ? "bg-fill-green-500" : "bg-fill-gray-400"}`} />
                                {skill.enabled ? "Enabled" : "Disabled"}
                              </span>
                              {skill.installed_at && (
                                <span className="text-[9px] text-ink-gray-400 dark:text-ink-gray-500">
                                  Installed {new Date(skill.installed_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {isAdmin && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingSkill({
                                        skill_id: skill.skill_id,
                                        skill_name: skill.skill_name,
                                        display_name: skill.display_name,
                                        description: skill.description || "",
                                        scope: skill.scope || "all",
                                        enabled: skill.enabled,
                                        credential_env_var: skill.credential_env_var || null,
                                      });
                                      setEditError(null);
                                    }}
                                    className="p-1.5 rounded-md hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 text-ink-gray-500 hover:text-ink-gray-700 dark:hover:text-ink-gray-300 transition-colors"
                                    title="Edit skill"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setRemoveConfirm({ skill_id: skill.skill_id, display_name: skill.display_name });
                                      setRemoveError(null);
                                    }}
                                    className="p-1.5 rounded-md hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 text-ink-gray-500 hover:text-ink-red-600 dark:hover:text-ink-red-400 transition-colors"
                                    title="Remove skill"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => setExpandedInstalledSkill((prev) => (prev === skill.skill_id ? null : skill.skill_id))}
                                className="p-1 rounded hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 transition-colors"
                              >
                                {expandedInstalledSkill === skill.skill_id
                                  ? <ChevronUp className="w-3.5 h-3.5 text-ink-gray-400" />
                                  : <ChevronDown className="w-3.5 h-3.5 text-ink-gray-400" />}
                              </button>
                            </div>
                          </div>

                          {/* SKILL.md expand panel */}
                          {expandedInstalledSkill === skill.skill_id && (
                            <div className="border-t border-edge-gray-100 dark:border-edge-gray-800 px-5 py-3">
                              {skill.description && (
                                <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2 leading-relaxed">{skill.description}</p>
                              )}
                              <InstalledSkillMd orgId={orgId} skill={skill} copiedSkill={copiedSkill} setCopiedSkill={setCopiedSkill} />
                            </div>
                          )}
                        </div>
                      ))}
                </SkillPackageGroup>
              );
            })}
          </div>
        );
      })()}

      {/* Edit skill modal */}
      {editingSkill && (
        <div
          className="fixed inset-0 z-[1000] bg-fill-black/50 dark:bg-fill-black/70 flex items-center justify-center px-4"
          onClick={() => { setEditingSkill(null); setEditError(null); }}
        >
          <div
            className="relative max-w-md w-full bg-fill-white dark:bg-fill-dialog-surface rounded-xl shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-ink-ink dark:text-ink-white mb-4">Edit Skill</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-ink-gray-400 dark:text-ink-gray-500 mb-1">
                  Skill Name <span className="font-normal">(read-only)</span>
                </label>
                <input
                  type="text"
                  value={editingSkill.skill_name}
                  disabled
                  className="photon-field w-full px-3 py-2 border border-edge-gray-200 dark:border-edge-gray-700 rounded-lg bg-fill-gray-50 dark:bg-fill-card-surface-dark text-sm text-ink-gray-400 dark:text-ink-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={editingSkill.display_name}
                  onChange={(e) => setEditingSkill((s) => ({ ...s, display_name: e.target.value }))}
                  className="photon-field w-full px-3 py-2 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-sm text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={editingSkill.description}
                  onChange={(e) => setEditingSkill((s) => ({ ...s, description: e.target.value }))}
                  rows={3}
                  className="photon-field w-full px-3 py-2 border border-edge-gray-300 dark:border-edge-gray-600 rounded-lg bg-fill-white dark:bg-fill-deep-surface text-sm text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-300 mb-1">Scope</label>
                <div className="flex items-center rounded overflow-hidden border border-edge-gray-200 dark:border-edge-gray-700 text-xs font-medium w-fit">
                  <button
                    type="button"
                    onClick={() => setEditingSkill((s) => ({ ...s, scope: "all" }))}
                    className={`px-3 py-2 transition-colors ${
                      editingSkill.scope === "all"
                        ? "bg-fill-primary text-ink-on-primary"
                        : "bg-fill-white dark:bg-fill-deep-surface text-ink-gray-500 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
                    }`}
                  >
                    Org-wide
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingSkill((s) => ({ ...s, scope: "agent_only" }))}
                    className={`px-3 py-2 transition-colors border-l border-edge-gray-200 dark:border-edge-gray-700 ${
                      editingSkill.scope === "agent_only"
                        ? "bg-fill-purple-600 text-ink-on-color"
                        : "bg-fill-white dark:bg-fill-deep-surface text-ink-gray-500 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
                    }`}
                  >
                    Agent only
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="skill-enabled"
                  checked={editingSkill.enabled}
                  onChange={(e) => setEditingSkill((s) => ({ ...s, enabled: e.target.checked }))}
                  className="w-4 h-4 text-ink-primary rounded focus:ring-focus-primary"
                />
                <label htmlFor="skill-enabled" className="text-sm font-medium text-ink-gray-700 dark:text-ink-gray-300">
                  Enable skill
                </label>
              </div>
              {editingSkill.credential_env_var && (
                <CredentialField
                  title="API Key"
                  description={`Required for this skill to authenticate as ${editingSkill.credential_env_var}.`}
                  fields={[{ key: "api_key", label: "API Key", placeholder: "Paste API key", type: "password" }]}
                  onFetch={() => getSkillCredential(orgId, user.uid, editingSkill.skill_id)}
                  onSave={async (form) => saveCredentialMutation.mutateAsync({
                    orgId, uid: user.uid, skillId: editingSkill.skill_id, apiKey: form.api_key.trim(),
                  })}
                />
              )}
            </div>

            {editError && <AlertBanner className="mt-4">{editError}</AlertBanner>}

            <div className="flex items-center gap-3 justify-end mt-5">
              <button
                type="button"
                onClick={() => { setEditingSkill(null); setEditError(null); }}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white border border-edge-gray-200 dark:border-edge-panel-border rounded-lg hover:bg-fill-gray-50 dark:hover:bg-fill-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-ink-on-primary bg-fill-primary hover:bg-fill-primary-hover rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updateMutation.isPending && <InlineSpinner />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove confirmation */}
      <ConfirmDialog
        open={!!removeConfirm && !removeBlockedAgents}
        title="Remove Skill?"
        error={removeError}
        confirmLabel="Remove"
        loadingLabel="Removing..."
        isLoading={removeMutation.isPending}
        onCancel={() => { setRemoveConfirm(null); setRemoveError(null); }}
        onConfirm={() => handleRemove(false)}
        backdropClose={!removeMutation.isPending}
      >
        {`Remove "${removeConfirm?.display_name}"? This will detach it from all agents that use it.`}
      </ConfirmDialog>

      {/* Remove blocked — skill in use */}
      <ConfirmDialog
        open={!!removeBlockedAgents}
        title="Skill In Use"
        confirmLabel="Remove Anyway"
        cancelLabel="Cancel"
        loadingLabel="Removing..."
        isLoading={removeMutation.isPending}
        onCancel={() => { setRemoveConfirm(null); setRemoveError(null); setRemoveBlockedAgents(null); }}
        onConfirm={() => handleRemove(true)}
        backdropClose={!removeMutation.isPending}
      >
        <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300 mb-2">
          <span className="font-medium">{removeConfirm?.display_name}</span> is attached to the following agents:
        </p>
        <ul className="text-sm text-ink-gray-600 dark:text-ink-gray-400 list-disc list-inside mb-3 space-y-0.5">
          {(removeBlockedAgents || []).map(a => (
            <li key={a.agent_id}>{a.name || a.agent_id}</li>
          ))}
        </ul>
        <p className="text-sm text-ink-gray-700 dark:text-ink-gray-300">Removing it will detach it from all of them.</p>
      </ConfirmDialog>
    </div>
  );
};

export default ForgeSkillsSection;
